<script setup lang="ts">
   import { computed } from 'vue';
   import { useRuntimeConfig } from '#imports';

   /**
    * Says out loud which environment this build is talking to.
    *
    * The staging app is deliberately the *same application* as production against a
    * different API — same code, same icon family, same everything a screenshot would show.
    * That is the point of it, and it is also what makes it dangerous: there is otherwise
    * nothing on screen to distinguish a build that takes test cards from one that takes real
    * ones. On a phone with both installed, the only other signal is the home-screen label,
    * which is gone the moment the app is open.
    *
    * So this shows the **API host**, not merely the word "staging". The failure it is really
    * guarding against is a build picking up the wrong `.env` — where the label would say
    * whatever the file claimed while the requests went somewhere else. The host is the thing
    * that cannot lie.
    *
    * Renders nothing in production, so it cannot leak into a client build.
    *
    * See docs/mobile-app-staging-build.md.
    */
   const config = useRuntimeConfig();

   const environment = computed(() => (config.public.siteEnv as string) || 'unknown');

   const isProduction = computed(() => environment.value === 'production');

   /**
    * Host only. The full base URL wraps onto three lines on a phone and buries the one part
    * that differs between environments.
    */
   const apiHost = computed(() => {
      const base = config.public.lmsApiBaseurl as string;

      if (!base) return 'no API configured';

      try {
         return new URL(base).host;
      } catch {
         return base;
      }
   });
</script>

<template>
   <div
      v-if="!isProduction"
      data-testid="environment-badge"
      class="flex items-center justify-center gap-2 bg-amber-400 px-3 py-1 text-center text-xs font-semibold uppercase tracking-wide text-amber-950">
      <span>{{ environment }}</span>
      <span class="font-mono normal-case opacity-80">{{ apiHost }}</span>
   </div>
</template>
